import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Select } from 'antd';
import moment, { Moment } from 'moment';
import cccApi from '../../api/cccApi';
import type { CccDashboardFilters, CccDashboardUser, CccDashboardWorkspace, CccTimeFilter } from './types';

const { RangePicker } = DatePicker;
const { Option } = Select;

const WORKSPACE_OPTIONS: Array<{ value: CccDashboardWorkspace; label: string }> = [
  { value: 'prospeccao', label: 'Prospeção' },
  { value: 'recuperacao', label: 'Recuperação' },
  { value: 'retencao', label: 'Reposição' },
];

type Props = {
  filters: CccDashboardFilters;
  onTimeFilterChange: (filter: CccTimeFilter) => void;
  onCustomDateRangeChange: (range: [string | null, string | null]) => void;
  onWorkspacesChange: (workspaces: CccDashboardWorkspace[]) => void;
  onUsersChange: (users: number[]) => void;
  onStatusesChange: (statuses: string[]) => void;
  onReset: () => void;
};

const CccClientsWorkedFilters: React.FC<Props> = ({
  filters,
  onTimeFilterChange,
  onCustomDateRangeChange,
  onWorkspacesChange,
  onUsersChange,
  onStatusesChange,
  onReset,
}) => {
  const [users, setUsers] = useState<CccDashboardUser[]>([]);
  const [statusOptions, setStatusOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    cccApi
      .getCccDashboardUsers()
      .then((res) => {
        if (cancelled) return;
        setUsers(Array.isArray(res.users) ? res.users : []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingStatuses(true);
    cccApi
      .getCccDashboardStatusOptions()
      .then((res) => {
        if (cancelled) return;
        setStatusOptions(Array.isArray(res.statuses) ? res.statuses : []);
      })
      .catch(() => {
        if (!cancelled) setStatusOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStatuses(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const workDayRange: [Moment | null, Moment | null] =
    filters.customDateRange[0] && filters.customDateRange[1]
      ? [moment(filters.customDateRange[0]), moment(filters.customDateRange[1])]
      : [null, null];

  const handleWorkDayChange = (dates: [Moment | null, Moment | null] | null) => {
    if (dates?.[0] && dates[1]) {
      onCustomDateRangeChange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD'),
      ]);
    } else {
      onCustomDateRangeChange([null, null]);
      if (filters.timeFilter === 'custom') onTimeFilterChange('today');
    }
  };

  const shortcutActive = (key: CccTimeFilter) =>
    filters.timeFilter === key && !!filters.customDateRange[0] && !!filters.customDateRange[1];

  const hasActiveFilters =
    filters.timeFilter !== 'today' ||
    filters.selectedWorkspaces.length > 0 ||
    filters.selectedUsers.length > 0 ||
    filters.selectedStatuses.length > 0;

  return (
    <div className="filters-panel">
      <div className="filters-header">
        <div className="filters-header-left">
          <span className="filters-icon">🔍</span>
          <h3>Clientes em tratamento</h3>
        </div>
      </div>
      <div className="filters-content">
        <div className="filters-main-row">
          <div className="time-filter-section">
            <label className="time-filter-label">Período:</label>
            <RangePicker
              value={workDayRange}
              onChange={handleWorkDayChange}
              format="DD/MM/YYYY"
              size="middle"
            />
            <div className="time-filter-buttons">
              {(['today', 'week'] as CccTimeFilter[]).map((key) => (
                <button
                  type="button"
                  key={key}
                  className="time-filter-shortcut"
                  onClick={() => onTimeFilterChange(key)}
                  style={{
                    backgroundColor: shortcutActive(key) ? '#dc2626' : '#f3f4f6',
                    color: shortcutActive(key) ? 'white' : '#374151',
                  }}
                >
                  {key === 'today' ? 'Hoje' : 'Esta Semana'}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group-compact">
            <label className="filter-label">Áreas</label>
            <Select<CccDashboardWorkspace[]>
              mode="multiple"
              value={filters.selectedWorkspaces}
              onChange={onWorkspacesChange}
              placeholder="Todas as áreas"
              style={{ width: 220 }}
              allowClear
              maxTagCount="responsive"
            >
              {WORKSPACE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="filter-group-compact">
            <label className="filter-label">Utilizadores</label>
            <Select<number[]>
              mode="multiple"
              value={filters.selectedUsers}
              onChange={onUsersChange}
              placeholder="Todos"
              style={{ width: 240 }}
              loading={loadingUsers}
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            >
              {users.map((u) => (
                <Option key={u.id} value={u.id} label={u.name}>
                  {u.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="filter-group-compact">
            <label className="filter-label">Estados</label>
            <Select<string[]>
              mode="multiple"
              value={filters.selectedStatuses}
              onChange={onStatusesChange}
              placeholder="Todos"
              style={{ width: 240 }}
              loading={loadingStatuses}
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            >
              {statusOptions.map((opt) => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="filters-actions">
          <Button type="default" onClick={onReset} disabled={!hasActiveFilters} size="large">
            Repor filtros
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CccClientsWorkedFilters;
